<template>
  <AdminLayout>
    <Head title="Approvals" />

    <template #pageTitle>Approvals</template>

    <template #pageTitleWidgets>
      <DataGridControlBar />
    </template>

    <template #pageAlerts>
      <SuccessFlash :flash="page.props.flash"></SuccessFlash>
    </template>

    <template #headingOptions>
      <div class="relative inline-flex self-start mt-5 mb-3">
        <Menu
          as="div"
          class="relative inline-block text-left ml-3"
          :class="{ 'opacity-0': !showingActionButton }"
        >
          <div>
            <MenuButton
              class="inline-flex items-center px-4 py-3 border bg-gray-100 text-sm font-medium rounded-md shadow-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-blue-600"
            >
              Actions
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50 overflow-hidden"
            >
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  @click="assignPackages({ type: ASSIGN_TYPES.BATCH })"
                  class="group w-full flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100"
                  :class="[active ? 'bg-gray-400 text-gray-900' : 'text-gray-700']"
                >
                  Assign Product
                </button>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  @click="openBulkSpaceModal"
                  class="group w-full flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100"
                  :class="[active ? 'bg-gray-400 text-gray-900' : 'text-gray-700']"
                >
                  Set Space
                </button>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  @click="publishPackages"
                  class="group w-full flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100"
                  :class="[active ? 'bg-gray-400 text-gray-900' : 'text-gray-700']"
                >
                  Publish
                </button>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  @click="approvePackages"
                  class="group w-full flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100"
                  :class="[active ? 'bg-gray-400 text-gray-900' : 'text-gray-700']"
                >
                  Approve
                </button>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  @click="rejectPackages"
                  class="group w-full flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100"
                  :class="[active ? 'bg-gray-400 text-gray-900' : 'text-gray-700']"
                >
                  Reject
                </button>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </template>

    <DataGrid
      name="approvals_data_table"
      :endpoint="route('api.tables.approvals')"
      :filters="form"
      :columns="columns"
      :block-size="40"
      :page-size="40"
      :show-check-column="true"
    />

    <AssignProductModal
      v-if="showAssignModal"
      :products="page.props.products"
      :errors="assignForm.errors"
      :submitting="assignForm.processing"
      @close="closeAssignModal"
      @assign="submitAssign"
    />

    <BulkAssignSpace
      v-if="showBulkSpaceModal"
      :spaces="page.props.spaces"
      :errors="spaceAssignForm.errors"
      :submitting="spaceAssignForm.processing"
      @close="closeBulkSpaceModal"
      @assign="submitAssignSpace"
    />
  </AdminLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Head, useForm } from '@inertiajs/vue3'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'
import PairProductCell from '@/Pages/Packages/Approvals/Components/PairProductCell.vue'
import DateCell from '@/Components/DataGrid/Cells/DateCell.vue'
import StatusCell from '@/Pages/Packages/Approvals/Components/StatusCell.vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import SuccessFlash from '@/Partials/Alerts/SuccessFlash'
import { usePage } from '@inertiajs/vue3'
import AssignProductModal from '@/Pages/Packages/Components/AssignProductModal.vue'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'
import BulkAssignSpace from '@/Pages/Packages/Components/BulkAssignSpace.vue'
import { useSelectedRowNodes } from '@/Components/DataGrid/Composables/selected-row-nodes'
import _ from 'lodash'
import { LANG } from '@/Pages/Deliveries/constants'
import { useErrors } from '@/Composables/errors'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'

const { showError } = useErrors()
const { context } = useInitGridState()
const page = usePage()
const form = useForm({})
const { show } = useConfirmDialog()

const { selectedRowNodes, selectedRowNodeIds, totalSelectedRowNodes } = useSelectedRowNodes({
  gridApi: computed(() => context.gridApi)
})

const ASSIGN_TYPES = {
  SINGLE: 'single',
  BATCH: 'batch'
}

const showAssignModal = ref(false)
const showBulkSpaceModal = ref(false)

const openAssignModal = () => (showAssignModal.value = true)
const closeAssignModal = () => (showAssignModal.value = false)

const openBulkSpaceModal = () => (showBulkSpaceModal.value = true)
const closeBulkSpaceModal = () => (showBulkSpaceModal.value = false)

const assignForm = useForm({
  product: null,
  packages: []
})

const spaceAssignForm = useForm({
  space: null,
  packages: []
})

// @todo: Make consistent with other forms; packages vs package_ids.
const actionForm = useForm({
  package_ids: []
})

const columns = ref([
  {
    headerName: 'ID',
    field: 'id',
    width: 80
  },
  {
    headerName: 'Sub-Lot',
    field: 'external_sub_lot_num',
    filter: 'agTextColumnFilter',
    cellRenderer: LinkCell,
    cellRendererParams: (params) => ({
      url: route('integrations.biotrack.inventory.show', { source_id: params.value }),
      display: params.value
    })
  },
  {
    headerName: 'Lot #',
    field: 'lot_num'
  },
  {
    headerName: 'Location',
    field: 'location.name'
  },
  {
    headerName: 'Room',
    field: 'space.name',
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Vendor',
    field: 'product.supplier.name',
    sortable: false
  },
  {
    headerName: 'Sync Date',
    cellRenderer: DateCell,
    field: 'created_at',
    filter: 'agDateColumnFilter'
  },
  {
    headerName: 'Tag #',
    field: 'tag_num'
  },
  {
    field: 'product.name',
    width: 250,
    headerName: 'Pair Product (SKU)',
    cellRenderer: PairProductCell,
    cellRendererParams: {
      actionFunction: (row) => {
        assignPackages({ type: ASSIGN_TYPES.SINGLE, id: row.id })
      }
    },
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Suggested',
    field: 'source_product_name',
    valueFormatter: (params) => params.value || '-',
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Source QTY',
    field: 'source_quantity',
    valueFormatter: (params) => (params.value ? params.value + ' ' + params.node.data.source_quantity_unit : '-')
  },
  {
    headerName: 'Initial Units',
    field: 'quantity'
  },
  {
    headerName: 'On-Hand',
    field: 'quantity_on_hand'
  },
  {
    headerName: 'Available',
    field: 'quantity_available'
  },
  {
    headerName: 'QA Status',
    field: 'lab_status',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: page.props.labStatusOptions
    }
  },
  {
    headerName: 'Status',
    field: 'publish_status',
    cellRenderer: StatusCell
  }
])

const showingActionButton = computed(() => totalSelectedRowNodes.value > 0)

const publishPackages = async () => {
  const [cancelled, _] = await settle(
    show({
      type: 'danger',
      title: 'Publish Packages',
      body: 'Are you sure you want to publish these packages?'
    })
  )
  if (!cancelled) {
    submitPublish()
  }
}

const approvePackages = async () => {
  const [cancelled, _] = await settle(
    show({
      type: 'danger',
      title: 'Approve Packages',
      body: 'Are you sure you want to approve these packages?'
    })
  )
  if (!cancelled) {
    submitApprove()
  }
}

const rejectPackages = async () => {
  const [cancelled, _] = await settle(
    show({
      type: 'danger',
      title: 'Reject Packages',
      body: 'Are you sure you want to reject these packages?'
    })
  )
  if (!cancelled) {
    submitReject()
  }
}

const assignPackages = ({ id, type }) => {
  if (type === ASSIGN_TYPES.SINGLE) {
    assignForm.packages = [id]
  } else {
    assignForm.packages = selectedRowNodeIds.value
  }
  openAssignModal()
}

const applyTransactionPreservingSelectedNodes = (transaction) => {
  const selectedNodesSnapshot = [...selectedRowNodes.value]

  const revertSelectedNodes = async () => {
    await new Promise((resolve) => setTimeout(resolve, 0))
    context.gridApi.setNodesSelected({ nodes: selectedNodesSnapshot, newValue: true })
  }

  context.gridApi.applyServerSideTransactionAsync(transaction, revertSelectedNodes)
}

const submitPublish = () => {
  actionForm.package_ids = selectedRowNodeIds.value

  actionForm.post(route('packages.bulk_publish'), {
    onSuccess: () => {
      const transaction = {
        update: selectedRowNodes.value.map((node) => ({
          ...node.data,
          id: String(node.data.id),
          publish_status: 'Published'
        }))
      }

      applyTransactionPreservingSelectedNodes(transaction)
    },
    onError: (errors) => {
      showError({ message: errors.package, useToast: true })
    }
  })
}

const submitAssign = ({ productId }) => {
  assignForm.product = productId

  assignForm.post(route('packages.bulk_assign'), {
    onSuccess: () => {
      // Find the product information in the available products data passed down by BE / Inertia.
      const product = page.props.products.find((product) => product.id === assignForm.product)

      // Get the nodes to process based on the package ids currently set in the assignForm
      const nodesToProcess = assignForm.packages.map((id) => context.gridApi.getRowNode(String(id)))

      const transaction = {
        update: nodesToProcess.map((node) => ({ ...node.data, id: String(node.data.id), product: product }))
      }

      applyTransactionPreservingSelectedNodes(transaction)

      closeAssignModal()
    }
  })
}

const submitAssignSpace = ({ spaceId }) => {
  spaceAssignForm.space = spaceId
  spaceAssignForm.packages = selectedRowNodeIds.value

  // Find the space information in the available spaces data passed down by BE / Inertia.
  const space = page.props.spaces.find((space) => space.id === spaceId)

  spaceAssignForm.post(route('packages.assign_space'), {
    onSuccess: () => {
      console.log('assign space success')
      const transaction = {
        update: selectedRowNodes.value.map((node) => ({
          ...node.data,
          id: String(node.data.id),
          location: {
            name: space.location.name
          },
          space: {
            name: space.name
          }
        }))
      }
      applyTransactionPreservingSelectedNodes(transaction)
      closeBulkSpaceModal()
    }
  })
}

const submitApprove = () => {
  actionForm.package_ids = selectedRowNodeIds.value

  actionForm.post(route('packages.approve'), {
    onSuccess: () => {
      console.log('approve success')
      const transaction = {
        remove: selectedRowNodeIds.value.map((id) => ({ id: String(id) }))
      }

      context.gridApi.applyServerSideTransaction(transaction)
    },
    onError: (errors) => {
      showError({ message: errors.package, useToast: true })
    }
  })
}

const submitReject = () => {
  actionForm.package_ids = selectedRowNodeIds.value

  actionForm.post(route('packages.reject'), {
    onSuccess: () => {
      const transaction = {
        remove: selectedRowNodeIds.value.map((id) => ({ id: String(id) }))
      }

      context.gridApi.applyServerSideTransaction(transaction)
    },
    onError: (errors) => {
      showError({ message: errors.package, useToast: true })
    }
  })
}
</script>
